import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { registry } from '../renderer/renderers';
import { LanguageContext } from '../LanguageContext';
import jsonLogic from 'json-logic-js';
import { useWorkflowSchema, WORKFLOW_SCHEMA_STATUS } from '../hooks/useWorkflowSchema';

function normalizeValue(comp, val) {
  const n = (comp && typeof comp.normalize === 'string') ? comp.normalize.toLowerCase() : '';
  switch (n) {
    case 'number': {
      const num = Number(val);
      return Number.isFinite(num) ? num : val;
    }
    case 'boolean': {
      if (val === true || val === false) return val;
      const s = String(val).toLowerCase();
      return s === 'true' || s === '1' || s === 'yes' || s === 'y';
    }
    default:
      return val;
  }
}

const FILE_UPLOAD_VALUE_KEYS = ['filePath', 'file_path', 'path', 'key', 'fileId', 'file_id', 'name', 'originalFilename', 'original_filename'];
const hasFileUploadValue = (val) => {
  if (val === undefined || val === null) return false;
  if (typeof val === 'string') return false;
  if (typeof val === 'number') return Number.isFinite(val);
  if (Array.isArray(val)) return val.some(hasFileUploadValue);
  if (typeof val === 'object') {
    return FILE_UPLOAD_VALUE_KEYS.some((k) => {
      const v = val[k];
      if (v === undefined || v === null) return false;
      if (typeof v === 'string') return v.trim() !== '';
      if (typeof v === 'number') return Number.isFinite(v);
      return false;
    });
  }
  return false;
};
const isFileUploadEmpty = (val) => !hasFileUploadValue(val);

function renderComponent(comp, lang, state, setState) {
  const type = String(comp.type || '').toLowerCase();
  const Cmp = registry[type];
  const key = comp.storageKey || comp.id;
  if (!Cmp) {
    return <div key={comp.id} className="govuk-hint">Unsupported component: {type}</div>;
  }
  const props = { comp, lang, values: state };
  // Wire value/onChange for input-like controls (those that accept these props)
  const needsValue = ['input', 'text', 'email', 'phone', 'password', 'number', 'textarea', 'radio', 'checkbox', 'select', 'date', 'character-count', 'file-upload', 'checkboxes'].includes(type);
  if (needsValue) {
    props.value = state[key];
    props.onChange = (val) => setState(s => ({ ...s, [key]: normalizeValue(comp, val) }));
  }
  // Provide a render helper for components that can render children (conditional reveal, etc.)
  props.render = (child) => renderComponent(child, lang, state, setState);
  return <Cmp key={comp.id} {...props} />;
}

export default function SchemaPreview() {
  const { language, setLanguage } = useContext(LanguageContext) || { language: 'en', setLanguage: () => {} };
  const lang = language;
  const { status: schemaStatus, schema: runtimeSchema, error: schemaError, refresh: refreshSchema } = useWorkflowSchema();
  const [values, setValues] = useState({});
  const [errors, setErrors] = useState({});
  const errorSummaryRef = useRef(null);
  const steps = useMemo(() => Array.isArray(runtimeSchema) ? runtimeSchema : [], [runtimeSchema]);
  const schemaReady = schemaStatus === WORKFLOW_SCHEMA_STATUS.READY && steps.length > 0;
  function validateStep(step) {
    const newErrors = {};
    (step.components || []).forEach(comp => {
      const key = comp.storageKey || comp.id;
      const v = values[key];
      if (comp.required) {
        const type = String(comp.type || '').toLowerCase();
        const empty = type === 'file-upload'
          ? isFileUploadEmpty(v)
          : (v === undefined || v === null || v === '' || (Array.isArray(v) && v.length === 0));
        if (empty) newErrors[key] = (comp.validation && comp.validation.errorMessage && (comp.validation.errorMessage[lang] || comp.validation.errorMessage)) || 'This field is required';
      }
      if (v && typeof v === 'string' && comp.validation) {
        const { minLength, pattern, errorMessage } = comp.validation;
        if (minLength && v.length < minLength) newErrors[key] = (errorMessage && (errorMessage[lang] || errorMessage)) || `Minimum length is ${minLength}`;
        if (pattern) {
          try { const re = new RegExp(pattern); if (!re.test(v)) newErrors[key] = (errorMessage && (errorMessage[lang] || errorMessage)) || 'Invalid format'; } catch (_) {}
        }
      }
    });
    setErrors(newErrors);
    return newErrors;
  }
  const [currentStepId, setCurrentStepId] = useState(null);
  const [showAll, setShowAll] = useState(false);
  useEffect(() => {
    if (showAll) return;
    if (steps.length === 0) {
      setCurrentStepId(null);
      return;
    }
    setCurrentStepId((prev) => {
      if (prev && steps.some(s => s.stepId === prev)) return prev;
      return steps[0]?.stepId || null;
    });
  }, [steps, showAll]);
  const currentStep = showAll ? null : (steps.find(s => s.stepId === currentStepId) || steps[0]);
  useEffect(() => {
    const hasErrors = Object.keys(errors || {}).length > 0;
    if (hasErrors && errorSummaryRef.current) {
      // Prefix page title with Error as per GOV.UK guidance
      if (!document.title.startsWith('Error:')) {
        document.title = `Error: ${document.title}`;
      }
      errorSummaryRef.current.focus();
    }
  }, [errors]);
  function anchorIdFor(comp) {
    const key = comp.storageKey || comp.id;
    const type = String(comp.type || '').toLowerCase();
    if (type === 'radio' || type === 'checkbox' || type === 'checkboxes') return `${key}-0`;
    if (type === 'date' || type === 'date-input') return `${key}-day`;
    return key;
  }
  const languages = useMemo(() => {
    // Derive available language keys present in schema labels/titles
    const set = new Set(['en', 'fr']);
    steps.forEach(step => {
      const t = step?.title;
      if (t && typeof t === 'object') Object.keys(t).forEach(k => set.add(k));
      (step.components || []).forEach(c => {
        const l = c?.label; const h = c?.hint; const tx = c?.text;
        [l, h, tx].forEach(obj => { if (obj && typeof obj === 'object') Object.keys(obj).forEach(k => set.add(k)); });
      });
    });
    return Array.from(set);
  }, [steps]);

  if (schemaStatus === WORKFLOW_SCHEMA_STATUS.LOADING || schemaStatus === WORKFLOW_SCHEMA_STATUS.IDLE) {
    return (
      <div className="govuk-width-container" style={{ marginTop: '2rem' }}>
        <p className="govuk-body">
          {language === 'fr' ? 'Chargement du schéma de formulaire...' : 'Loading workflow schema...'}
        </p>
      </div>
    );
  }

  if (schemaStatus === WORKFLOW_SCHEMA_STATUS.ERROR) {
    const applicantAccountRequired = schemaError === 'applicant_account_required';
    const message = applicantAccountRequired
      ? (language === 'fr'
        ? 'Cet aperçu est réservé aux comptes du portail des demandeurs. Déconnectez-vous, puis reconnectez-vous avec un compte demandeur.'
        : 'This preview is for applicant portal accounts. Please sign out, then sign in with an applicant account.')
      : (language === 'fr'
        ? 'Le schéma de formulaire n’a pas pu être chargé. Réessayez plus tard ou contactez le soutien.'
        : 'The workflow schema could not be loaded. Please try again later or contact support.');
    return (
      <div className="govuk-width-container" style={{ marginTop: '2rem' }}>
        <div className="govuk-warning-text">
          <span className="govuk-warning-text__icon" aria-hidden="true">!</span>
          <strong className="govuk-warning-text__text">
            {message}
            {schemaError && !applicantAccountRequired ? ` (${schemaError})` : ''}
          </strong>
          {!applicantAccountRequired && (
            <div className="govuk-!-margin-top-2">
              <button type="button" className="govuk-button govuk-button--secondary" onClick={() => refreshSchema()}>
                {language === 'fr' ? 'Réessayer le chargement' : 'Try loading again'}
              </button>
            </div>
          )}
        </div>
      </div>
    );
  }

  if (!schemaReady) {
    return (
      <div className="govuk-width-container" style={{ marginTop: '2rem' }}>
        <div className="govuk-warning-text">
          <span className="govuk-warning-text__icon" aria-hidden="true">!</span>
          <strong className="govuk-warning-text__text">
            {language === 'fr'
              ? 'Aucun schéma de formulaire publié n’est disponible.'
              : 'No published workflow schema is available.'}
          </strong>
        </div>
      </div>
    );
  }

  return (
    <div className="govuk-width-container">
      <h1 className="govuk-heading-l">Schema Preview</h1>
      <div className="govuk-!-margin-bottom-4">
        <div className="govuk-form-group">
          <label className="govuk-label" htmlFor="lang-select">Language</label>
          <select id="lang-select" className="govuk-select" value={lang} onChange={(e) => setLanguage(e.target.value)}>
            {languages.map(code => (
              <option key={code} value={code}>{code}</option>
            ))}
          </select>
        </div>
        <div className="govuk-form-group">
          <div className="govuk-checkboxes" data-module="govuk-checkboxes">
            <div className="govuk-checkboxes__item">
              <input className="govuk-checkboxes__input" id="show-all" type="checkbox" checked={showAll} onChange={(e) => setShowAll(e.target.checked)} />
              <label className="govuk-label govuk-checkboxes__label" htmlFor="show-all">Show all steps</label>
            </div>
          </div>
        </div>
      </div>
      {!showAll && currentStep && (
        <section key={currentStep.stepId} className="govuk-!-margin-bottom-6">
          <h2 className="govuk-heading-m">{currentStep?.title?.[lang] || currentStep?.title || currentStep.stepId}</h2>
          {Object.keys(errors).length > 0 && (
            <div ref={errorSummaryRef} tabIndex="-1" className="govuk-error-summary" aria-labelledby="error-summary-title" role="alert" data-module="govuk-error-summary">
              <h2 className="govuk-error-summary__title" id="error-summary-title">There is a problem</h2>
              <div className="govuk-error-summary__body">
                <ul className="govuk-list govuk-error-summary__list">
                  {Object.entries(errors).map(([k, m]) => {
                    const comp = (currentStep.components || []).find(c => (c.storageKey || c.id) === k);
                    const anchor = comp ? anchorIdFor(comp) : k;
                    return (<li key={k}><a href={`#${anchor}`}>{m}</a></li>);
                  })}
                </ul>
              </div>
            </div>
          )}
          {(currentStep.components || []).map(comp => {
            const key = comp.storageKey || comp.id;
            const type = String(comp.type || '').toLowerCase();
            const Cmp = registry[type];
            if (!Cmp) return renderComponent(comp, lang, values, setValues);
            const props = { comp, lang, values };
            const needsValue = ['input', 'text', 'email', 'phone', 'password', 'number', 'textarea', 'radio', 'checkbox', 'select', 'date', 'character-count', 'file-upload', 'checkboxes'].includes(type);
            if (needsValue) {
              props.value = values[key];
              props.onChange = (val) => setValues(s => ({ ...s, [key]: normalizeValue(comp, val) }));
              props.error = errors[key];
            }
            props.render = (child) => renderComponent(child, lang, values, setValues);
            return <Cmp key={comp.id} {...props} />;
          })}
          <div className="govuk-button-group govuk-!-margin-top-4">
            <button type="button" className="govuk-button" data-module="govuk-button" onClick={() => {
              const errs = validateStep(currentStep);
              if (Object.keys(errs).length > 0) return;
              let next = currentStep.nextStepId || currentStep.defaultNextStepId;
              if (Array.isArray(currentStep.branching) && currentStep.branching.length > 0) {
                for (const br of currentStep.branching) {
                  try { if (jsonLogic.apply(br.condition, values)) { next = br.nextStepId; break; } } catch (_) {}
                }
              }
              if (next) setCurrentStepId(next);
            }}>Next</button>
            <button type="button" className="govuk-button govuk-button--secondary" data-module="govuk-button" onClick={() => {
              // naive previous: find the step that points to me as nextStepId
              const prev = steps.find(s => (s.nextStepId || s.defaultNextStepId) === currentStep.stepId);
              if (prev) setCurrentStepId(prev.stepId);
            }}>Previous</button>
          </div>
        </section>
      )}
      {showAll && steps.map(step => (
        <section key={step.stepId} className="govuk-!-margin-bottom-6">
          <h2 className="govuk-heading-m">{step?.title?.[lang] || step?.title || step.stepId}</h2>
          {(step.components || []).map(comp => renderComponent(comp, lang, values, setValues))}
        </section>
      ))}
      <section className="govuk-!-margin-top-6">
        <h3 className="govuk-heading-s">Debug values</h3>
        <pre className="govuk-body" style={{ background: '#f3f2f1', padding: '12px', borderRadius: 4 }}>
{JSON.stringify(values, null, 2)}
        </pre>
      </section>
    </div>
  );
}
